"use client";

import React from "react";
import { Activity, Radio, Layers, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ApplicationItem {
  id: string;
  title: string;
  category: string;
  description: string;
  summary: string;
  highlights: string[];
  linkUrl: string;
  linkText: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const applications: ApplicationItem[] = [{
  id: "blend-monitoring",
  title: "Hazardous-Area Blend Monitoring",
  category: "Solid Dosage & Powder Processing",
  description: "Direct measurement of blend homogeneity inside operating vessels and tumble blenders to detect real-time mixing endpoints.",
  summary: "Eliminates invasive thief sampling by integrating non-destructive spectroscopic interfaces directly onto active process vessels.",
  highlights: ["In-situ interface designs for sealed process vessels", "Real-time homogeneity and endpoint determination", "Reduced operator exposure during powder sampling"],
  linkUrl: "/BlendUniformityPage",
  linkText: "Explore Blend Uniformity",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png-wvc-sizes",
  imageAlt: "Hazardous-area blend monitoring process interface"
}, {
  id: "wireless-installations",
  title: "Wireless Process Installations",
  category: "Flexible Plant Integration",
  description: "Battery-operated and wireless optical sensors designed for rotating drums, portable bins, and complex plant layouts.",
  summary: "Provides analytical coverage in locations where physical cabling is impractical or restricted by moving machinery.",
  highlights: ["Cable-free communication for rotating process equipment", "Secure data transmission to central manufacturing systems", "Streamlined mechanical installation with low footprint"],
  linkUrl: "/ProcessAnalyticalTechnologyPage",
  linkText: "View PAT Solutions",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_sensor.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_sensor.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_sensor.png-wvc-sizes",
  imageAlt: "Wireless process spectrometer installed on industrial equipment"
}, {
  id: "granulation-drying",
  title: "Continuous Granulation & Drying Control",
  category: "Fluid Bed & Drying Operations",
  description: "In-line moisture, solvent concentration, and particle growth monitoring during active fluid bed or continuous drying runs.",
  summary: "Tracks moisture evolution continuously, allowing precise endpoint detection and consistent batch-to-batch product moisture targets.",
  highlights: ["Continuous tracking of moisture content and solvent levels", "Accurate drying endpoint detection to prevent over-processing", "Integration with fluid bed dryers and continuous granulators"],
  linkUrl: "/DryingEndpointPage",
  linkText: "Explore Drying Endpoints",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_drying_process.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_drying_process.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_drying_process.png-wvc-sizes",
  imageAlt: "Fluid bed drying monitoring interface and probe"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="processapplications" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Process Analytical Technology
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Typical Process Applications
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Analytical instrumentation configured for production environments, qualification demands, and continuous process verification across regulated facilities in Ireland and the UK.
          </p>
        </div>

        {/* Applications Grid */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {applications.map((app, index) => <motion.div key={app.id} data-index={index} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 w-full overflow-hidden">
                <div>
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                    <img src={app.imageSrc} data-wvc-srcset={app.imageSrcset} data-wvc-sizes={app.imageSizes} alt={app.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-background/90 text-foreground backdrop-blur-xs border border-border">
                        {app.category}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <CardTitle className="text-xl font-semibold text-foreground leading-snug">
                      {app.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-2 leading-relaxed">
                      {app.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {app.summary}
                    </p>

                    <Separator />

                    <div className="space-y-2.5 pt-1">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Implementation Aspects
                      </span>
                      <ul className="space-y-2">
                        {app.highlights.map((highlight, hIndex) => <li key={hIndex} data-index={hIndex} className="flex items-start gap-2 text-xs text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border mt-auto">
                  <Button variant="outline" asChild className="w-full justify-between">
                    <Link to={app.linkUrl}>
                      <span>{app.linkText}</span>
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Support Banner */}
        <div className="rounded-xl border border-border bg-secondary/50 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">
                Application Feasibility & Consultation
              </h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our engineering specialists evaluate process optical paths, mounting constraints, and regulatory requirements before commissioning.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button variant="outline" asChild>
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
            <Button variant="default" asChild>
              <Link to="/BookADemoPage">
                Book a Demo
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}